// Shared record-set → result builder. Given two record-sets matched by KEY, it
// produces the locked contract (add/remove/modify + a "row order changed" note)
// and the key-aligned `records` render. Used by BOTH the CSV engine and the
// JSON/YAML array-of-objects path, so an array of records diffs exactly like the
// same data as CSV — matched by id, order-insensitive, no spurious changes.
import { diffRecordSet } from './diff.js';

export function buildRecordsResult({ lm, rm, options = {}, leftCols, rightCols, schemaLeftCols = leftCols, schemaRightCols = rightCols, keyCol, format, rawRows, warning = null }) {
  // Schema-level column changes: a column present on ONE side only is a single
  // schema edit ("column b removed"), not per-row noise on every record. Columns
  // present on both (even sparsely) stay per-record. A selected key is still a
  // real schema column and must be reported if it disappears from either side.
  const schemaRemoved = schemaLeftCols.filter((c) => !schemaRightCols.includes(c));
  const schemaAdded = schemaRightCols.filter((c) => !schemaLeftCols.includes(c));
  const columns = [...new Set([...leftCols, ...rightCols])];

  // Fold schema columns into the ignore set so a record changed ONLY by a
  // whole-column add/remove isn't counted as a modified record. Its cells remain visible.
  const ignoreFields = [...(options.ignoreFields || []), ...schemaRemoved, ...schemaAdded];
  const res = diffRecordSet(lm, rm, { ...options, ignoreFields });

  const modified = res.matched.filter((m) => m.changed);
  const unchanged = res.matched.filter((m) => !m.changed);
  // Matched keys in each side's order, sorted ONCE: the same two lists decide
  // whether the relative order changed and which rows are marked reordered.
  const leftOrder = [...res.matched].sort((a, b) => a.leftIndex - b.leftIndex);
  const rightOrder = [...res.matched].sort((a, b) => a.rightIndex - b.rightIndex);
  const orderChanged = leftOrder.some((m, i) => m.key !== rightOrder[i].key);

  const changes = [];
  if (warning) changes.push({ kind: 'keywarn', key: warning.key });
  for (const name of schemaRemoved) changes.push({ kind: 'column-remove', name });
  for (const name of schemaAdded) changes.push({ kind: 'column-add', name });
  for (const m of modified) {
    changes.push({
      kind: 'record-modify',
      recordKey: m.key,
      cells: m.fieldChanges.filter(isCellChange).map((c) => ({ name: c.path, oldValue: c.oldValue, newValue: c.newValue })),
    });
  }
  for (const a of res.added) changes.push({ kind: 'record-add', recordKey: a.key });
  for (const r of res.removed) changes.push({ kind: 'record-remove', recordKey: r.key });
  if (orderChanged) changes.push({ kind: 'reorder' });

  const rows = buildRows(res, modified, unchanged, columns, keyCol);
  const ranks = new Map(rightOrder.map((m, i) => [m.key, i]));
  const positions = new Map(leftOrder.map((m, i) => [m.key, { oldPosition: m.leftIndex + 1, newPosition: m.rightIndex + 1, reordered: ranks.get(m.key) !== i }]));
  for (const row of res.added) positions.set(row.key, { newPosition: row.index + 1 });
  for (const row of res.removed) positions.set(row.key, { oldPosition: row.index + 1 });
  for (const row of rows) {
    Object.assign(row, positions.get(row.key));
    row.schemaChanged = schemaAdded.length > 0 || schemaRemoved.length > 0;
    for (const cell of row.cells) cell.schema = schemaAdded.includes(cell.name) ? 'add' : schemaRemoved.includes(cell.name) ? 'remove' : null;
  }

  return {
    format,
    keyColumn: warning ? null : keyCol, // positional fallback has no meaningful key
    warning: warning ? warning.kind : undefined,
    added: res.added.length,
    removed: res.removed.length,
    modified: modified.length,
    moved: orderChanged ? 1 : 0,
    unchanged: unchanged.length,
    columnsAdded: schemaAdded.length,
    columnsRemoved: schemaRemoved.length,
    changes,
    render: { kind: 'records', columns, leftColumns: leftCols, rightColumns: rightCols, keyColumn: keyCol, rows, rawRows },
  };
}

// Rows displayed as a ledger, sorted by key so the reshuffle isn't shown as
// churn. Position metadata lets the UI mark reordering without counting edits.
function buildRows(res, modified, unchanged, columns, keyCol) {
  const dataCols = columns.filter((c) => c !== keyCol);
  const single = (fields) => dataCols.map((c) => ({ name: c, oldValue: fields[c], newValue: fields[c], changed: false }));
  const pair = (m) => {
    const changed = new Set(m.fieldChanges.filter(isCellChange).map((c) => c.path));
    return dataCols.map((c) => ({ name: c, oldValue: m.leftFields[c], newValue: m.rightFields[c], changed: changed.has(c) }));
  };

  const rows = [];
  for (const m of modified) rows.push({ status: 'modify', key: m.key, cells: pair(m) });
  for (const m of unchanged) rows.push({ status: 'context', key: m.key, cells: pair(m) });
  for (const a of res.added) rows.push({ status: 'add', key: a.key, cells: single(a.fields) });
  for (const r of res.removed) rows.push({ status: 'remove', key: r.key, cells: single(r.fields) });

  rows.sort((a, b) => compareKeys(a.key, b.key));
  return rows;
}

// A field-level change worth showing as a cell: value change, type break, or a
// column present on only one side (added/removed for this record).
const isCellChange = (c) => c.kind === 'change' || c.kind === 'type' || c.kind === 'add' || c.kind === 'remove';

// Integer-looking keys sort numerically (so `10` follows `9`), but via BigInt:
// 64-bit ids past Number.MAX_SAFE_INTEGER would otherwise compare as equal and
// sort arbitrarily. Anything non-integer — including '' and 'false', which
// Number() silently turns into 0 — sorts as text (REL-060).
const INTEGER_KEY = /^[+-]?\d+$/;

function compareKeys(a, b) {
  const sa = String(a);
  const sb = String(b);
  if (INTEGER_KEY.test(sa) && INTEGER_KEY.test(sb)) {
    const ba = BigInt(sa);
    const bb = BigInt(sb);
    return ba < bb ? -1 : ba > bb ? 1 : 0;
  }
  return sa.localeCompare(sb);
}
